import { useDataActions } from 'hooks'
import { MEDICAL_EVENTS } from 'navigation/CONSTANTS'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from 'services/context/toast'
import {
  MEDICAL_EVENT_REGISTRATION,
  MEDICAL_EVENT_REGISTRATION_FILTERED
} from 'services/medical_event/CONSTANTS'
import { IPostMedicalEvent } from 'types'

const getToastMessage = (isEdition: boolean = false) => {
  if (isEdition) {
    return {
      success: 'Evento editado com sucesso',
      error: ['Houve um erro ao tentar editar o evento']
    }
  }

  return {
    success: 'Evento adicionado com sucesso',
    error: ['Houve um erro ao tentar adicionar o evento', 'Evento já existe']
  }
}

const fieldsNotNull: Array<keyof IPostMedicalEvent> = ['event_name']

export const useMedicalEventAction = () => {
  const { medicalEventId } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [openEditModal, setOpenEditModal] = useState<boolean>(false)

  const {
    sendRequest,
    state,
    errorsMap,
    setState,
    setErrorsMap,
    resetInputError,
    setData,
    data,
    ...hooks
  } = useDataActions<IPostMedicalEvent>({
    patchApi: () => MEDICAL_EVENT_REGISTRATION_FILTERED(medicalEventId),
    postApi: () => MEDICAL_EVENT_REGISTRATION,
    defaultValue: {
      event_name: '',
      description: ''
    },
    formatData: (originData): IPostMedicalEvent => {
      const formatEditMode = (
        originData: IPostMedicalEvent,
        data: IPostMedicalEvent
      ) => {
        const result: IPostMedicalEvent = {} as IPostMedicalEvent
        Object.keys(data).forEach(index => {
          const key = index as keyof IPostMedicalEvent
          const origin = originData[key]
          const value = data[key]
          if (origin != value) {
            result[key] = (
              typeof value === 'string' ? value?.toString().trim() : value
            ) as never
          }
        })
        return result
      }

      const formatNonEditMode = (data: IPostMedicalEvent) => {
        const result: IPostMedicalEvent = {} as IPostMedicalEvent
        Object.keys(data).forEach(index => {
          const key = index as keyof IPostMedicalEvent
          const value = data[key]
          const current = typeof value === 'string' ? value.trim() : value
          result[key] = (current === '' ? undefined : current) as never
        })
        return result
      }

      return hooks.editMode
        ? formatEditMode(originData, data)
        : formatNonEditMode(data)
    },
    fieldsNotNull
  })

  type ErrorMap = typeof errorsMap

  const validateForm = (): ErrorMap => {
    const newErrorsMap: ErrorMap = new Map()

    const { event_name } = data

    if (event_name.length < 2) {
      if (!hooks.editMode && event_name.length == 0) {
        newErrorsMap.set('event_name', 'Digite o nome do evento')
      } else {
        newErrorsMap.set(
          'event_name',
          'O nome do evento deve ter ao menos 2 caracteres'
        )
      }
    }

    errorsMap.clear()

    setErrorsMap(newErrorsMap)

    return newErrorsMap
  }

  const onSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    event.stopPropagation()

    const errors = validateForm()
    if (state == 'loading' || errors.size > 0) return

    const messages = getToastMessage(hooks.editMode)

    try {
      await sendRequest()

      setState('success')
      toast.handler({
        content: messages['success']
      })
      navigate(`/${MEDICAL_EVENTS}`, {
        replace: true
      })
    } catch (e) {
      setState('error')
      let error: number
      if (e instanceof Error) {
        error = e.message == 'duplicate' ? 1 : 0
      } else {
        error = 0
      }
      toast.handler({
        content: messages['error'][error],
        severity: 'error'
      })
    }
    setOpenEditModal(false)
  }

  return {
    ...hooks,
    onSubmit,
    errorsMap,
    state,
    disableFields: state == 'loading',
    data,
    openEditModal,
    setOpenEditModal
  }
}
